import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type InvestorActivityRow = {
  id: string;
  name: string;
  email: string;
  companyName: string;
  isOnline: boolean;
  lastSeenAt: string | null;
  lastLoginAt: string | null;
  currentPage: string;
  viewedCount: number;
  viewedNames: string[];
  favoritedCount: number;
  favoritedNames: string[];
  messagedCount: number;
  messagedNames: string[];
  otherPages: string[];
  device?: 'phone' | 'tablet' | 'desktop' | null;
  phoneProfileNames?: string[];
  phonePitchDeckNames?: string[];
};

const DEVICE_LABEL: Record<'phone' | 'tablet' | 'desktop', string> = {
  phone: 'Phone',
  tablet: 'Tablet',
  desktop: 'Desktop',
};

function PhoneVisits({
  profileNames,
  pitchDeckNames,
}: {
  profileNames: string[];
  pitchDeckNames: string[];
}) {
  if (profileNames.length === 0 && pitchDeckNames.length === 0) {
    return <>—</>;
  }

  return (
    <div className="space-y-1">
      {profileNames.length > 0 ? (
        <div>
          Profile <CountWithTooltip count={profileNames.length} names={profileNames} />
        </div>
      ) : null}
      {pitchDeckNames.length > 0 ? (
        <div>
          Pitch deck <CountWithTooltip count={pitchDeckNames.length} names={pitchDeckNames} />
        </div>
      ) : null}
    </div>
  );
}

const CELL = 'border border-[#6e6e6e] px-3 py-2.5 align-top text-white';
const HEAD = `${CELL} bg-[#2f2f2f] font-medium`;

function formatLastSeen(iso: string | null, isOnline: boolean): string {
  if (isOnline) return 'Online';
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} (${d.getFullYear()})`;
}

function CountWithTooltip({ count, names }: { count: number; names: string[] }) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const closeTimer = useRef<number | null>(null);

  const show = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hide = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 160);
  };

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const width = 168;
    const left = Math.min(
      Math.max(12, rect.left + rect.width / 2 - width / 2),
      window.innerWidth - width - 12
    );
    const top = rect.bottom + 6;
    setPos({ top, left });
  }, [open]);

  if (count <= 0) {
    return <span>0</span>;
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="text-white underline underline-offset-2"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {count}
      </button>
      {open
        ? createPortal(
            <div
              className="fixed z-[80] min-w-[7.5rem] rounded-md bg-[#d8d8d8] px-3 py-2 text-left text-sm leading-snug text-[#222] shadow-md"
              style={{ top: pos.top, left: pos.left, width: 168 }}
              onMouseEnter={show}
              onMouseLeave={hide}
            >
              <ul className="m-0 list-none space-y-0.5 p-0">
                {names.map((name, index) => (
                  <li key={`${name}-${index}`} className="break-words">
                    {name}
                  </li>
                ))}
              </ul>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

const InvestorActivityTable: React.FC<{ rows: InvestorActivityRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return <p className="text-center text-gray-400">No investor activity yet.</p>;
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-[#6e6e6e]">
      <table className="w-full min-w-[1100px] border-collapse text-left text-sm text-white">
        <thead>
          <tr>
            <th className={HEAD}>Investor Name</th>
            <th className={`${HEAD} w-[11rem]`}>Online (or last login)</th>
            <th className={`${HEAD} w-[8rem]`}>Device</th>
            <th className={`${HEAD} w-[14rem]`}>Current page (or last page viewed)</th>
            <th className={`${HEAD} text-center`}>Viewed</th>
            <th className={`${HEAD} text-center`}>Following</th>
            <th className={`${HEAD} text-center`}>Messaged</th>
            <th className={HEAD}>On a phone</th>
            <th className={HEAD}>Other pages viewed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.id} className="bg-[#262626]">
              <td className={CELL}>{row.name}</td>
              <td className={CELL}>
                {row.isOnline ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-[#3ddc84]" />
                    Online
                  </span>
                ) : (
                  formatLastSeen(row.lastLoginAt || row.lastSeenAt, false)
                )}
              </td>
              <td className={CELL}>{row.device ? DEVICE_LABEL[row.device] : '—'}</td>
              <td className={`${CELL} break-words`}>{row.currentPage || '—'}</td>
              <td className={`${CELL} text-center`}>
                <CountWithTooltip count={row.viewedCount} names={row.viewedNames} />
              </td>
              <td className={`${CELL} text-center`}>
                <CountWithTooltip count={row.favoritedCount} names={row.favoritedNames} />
              </td>
              <td className={`${CELL} text-center`}>
                <CountWithTooltip count={row.messagedCount} names={row.messagedNames} />
              </td>
              <td className={CELL}>
                <PhoneVisits
                  profileNames={row.phoneProfileNames || []}
                  pitchDeckNames={row.phonePitchDeckNames || []}
                />
              </td>
              <td className={`${CELL} break-words`}>
                {row.otherPages.length > 0 ? row.otherPages.join(', ') : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InvestorActivityTable;
